import React from 'react';
import { IonApp, setupIonicReact } from '@ionic/react';
import { useAlert } from 'utils/alert';
import { _store, _update } from '_Store';
import { func, axius, types, services, env, analytics } from 'utils';
import { Loading } from 'components';
import Rautes from 'Rautes';
import { useLocation } from 'react-router';

// ::: plugins
import OneSignal from 'onesignal-cordova-plugin';
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import posthog from 'posthog-js';

import './assets/scss/custom.scss';
import ItemBottomModal from 'components/ItemBottmModal';

setupIonicReact();

const App: React.FC = () => {
	const [presentAlert] = useAlert();
	const lokation = useLocation();

	const {
		__auth: { user, isLoggedIn },
		__data: {
			settings: { countries },
			activeStore,
			location,
			cache,
			modalItem,
		},
	} = _store.useState();

	const [loading, setLoading] = React.useState(true);

	React.useEffect(() => {
		analytics.trackEvent(analytics.eventTypes.PAGE_VIEW, {
			page_path: lokation.pathname + lokation.search,
		});
	}, [lokation]);

	React.useEffect(() => {
		const [navigationEntry] = performance.getEntriesByType(
			'navigation'
		) as PerformanceNavigationTiming[];
		const launchType =
			navigationEntry?.type === 'navigate' ? 'cold_start' : 'warm_start';
		analytics.trackEvent(analytics.eventTypes.APP_LAUNCH, {
			launch_type: launchType,
		});

		setupPlugins();

		userDetails();
		getSettings();

		setInterval(
			() => {
				userDetails();
				getSettings();
			},
			10 * 60 * 1000
		); // 10 minutes

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const checkOrRequestNotifictaion = async () => {
		const granted = await PushNotifications.checkPermissions();
		const ios = await OneSignal.Notifications.permissionNative();
		if (granted.receive === 'granted' || ios > 1) {
			if (user?.id) {
				OneSignal.login(user.id);
				OneSignal.User.addSms(user.phoneNumber);
				OneSignal.User.addEmail(user.email);
				OneSignal.User.addTags({
					type: 'AARIN',
					version: func.app.version,
					platform: func.config.platform,
				});
			}
		} else {
			await PushNotifications.requestPermissions();
			checkOrRequestNotifictaion();
		}
	};

	const setupPlugins = async () => {
		if (func.isMobile) {
			await StatusBar.setOverlaysWebView({ overlay: false });
			await StatusBar.setStyle({ style: Style.Light });
			// Onesignal
			OneSignal.initialize(env.ONESIGNAL_ID);
			OneSignal.Notifications.addEventListener('click', (jsonData) => {
				console.log('onesignal click event', JSON.stringify(jsonData));
			});
		}
	};

	const setupTools = () => {
		// posthog
		if (user?.id) {
			const person = {
				id: user.id,
				name: user.name,
				email: user.email,
				phoneNumber: user.phoneNumber,
			};
			posthog.identify(user.id, person);
			posthog.setPersonProperties(person);
		}
	};

	React.useEffect(() => {
		if (func.isMobile) {
			checkOrRequestNotifictaion();
		}

		if (countries?.length === 0) {
			setLoading(true);
		} else {
			setLoading(false);
		}

		setupTools();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	React.useEffect(() => {
		cache.user && userDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cache.user]);

	React.useEffect(() => {
		if (activeStore.code) {
			func.getStorage('new_venue') && setLoading(true);
			services.categories(activeStore).then((categories) => {
				_update.data(
					'categories',
					categories
						.sort((a, b) => +a.sequence - +b.sequence)
						.filter((ctg) => !ctg.name.toLowerCase().includes('other'))
				);
				setLoading(false);
				func.delStorage('new_venue');
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStore]);

	React.useEffect(() => {
		const isLogin = func.getStorage('after_login');
		//  && !window.location.search
		if (location.address.x) {
			!isLogin && setLoading(true);
			const oldVenue = func.copyObject(activeStore)?.id;
			axius
				.post(`search?App`, {
					city: location.address.city,
					latitude: location.address.x,
					longitude: location.address.y,
					resultTypes: ['stores'],
					deliveryOptionsX: [location.option],
				})
				.then((res) => {
					if ((res.stores || []).length > 0) {
						if (!oldVenue && !isLogin && isLoggedIn) {
							func.setStorage('new_venue', 'yes');
							// window.location = '/';
							// _update.data('activeStore', res.stores[0]);
						}
						_update.data('stores', res.stores);
					} else {
						_update.data('stores', []);
						_update.data('activeStore', {});
						// const activeVenue = func.getStorageJson('activeStore');
						// if (activeVenue.id) {
						// 	func.delStorage('activeStore');
						// 	_update.data('activeStore', {});
						// 	// func.redirect('/');
						// }
					}
					setLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.address.x, cache.stores]);

	const userDetails = () => {
		Promise.all([
			axius.get(`users/me`, { file: 'App' }),
			axius.get(`users/me/favorites`, { file: 'App' }),
			axius.get(`promotions`, { file: 'App', status: 'ENABLED' }),
		]).then((res) => {
			if (res[0].status === 401 && user.id) {
				axius.signout(_update);
			}
			res[0].status === 200 && _update.auth('user', res[0].data);
			res[1].status === 200 && _update.auth('favorites', res[1].data);
			res[2].status === 200 &&
				_update.data(
					'promotions',
					(res[2].data as types.Promotion[]).filter(
						(promo) =>
							promo.isActive &&
							promo.providers.includes(types.OrderOrigins.REPDASH)
					)
				);
			setLoading(false);
		});
	};

	const getSettings = () => {
		axius.get('settings', { file: 'App' }).then((res) => {
			if (res.status === 200) {
				_update.data('settings', res.data);
				_update.data('deliveryPartners', res.data.deliveryPartners);
				const oldVersion = func.versionBreak(func.app.version);
				const newVersion = func.versionBreak(
					res.data?.version[func.config.platform]
				);
				if (newVersion > oldVersion) {
					if (func.isMobile) {
						presentAlert({
							header: `Update your application`,
							message: `A brand new version of the "Republic Dash" app is available in the Play Store. Please update your app to have a better experience.`,
							buttons: [
								{
									text: `Update now`,
									handler: () =>
										func.redirect(
											`https://play.google.com/store/apps/details?id=com.republic.dash.app`
										),
								},
							],
							backdropDismiss: false,
						});
					} else {
						func.clearCache();
					}
				}
			}
		});
	};

	if (loading) {
		return <Loading bg="#fff" />;
	}

	return (
		<IonApp>
			<Rautes />

			{modalItem?.id && (
				<ItemBottomModal
					isItemModalOpen={!!modalItem}
					setOpenedItemModal={() => _update.data('modalItem', {})}
					selectedProduct={modalItem}
				/>
			)}
		</IonApp>
	);
};

export default App;
